/**
 * Expense Service - Business Logic for Expense Management
 * 
 * Pure functions for expense operations and calculations.
 * Following dev guide: no DOM imports, deterministic operations.
 */

import { 
  Group, 
  Expense, 
  ParticipantShare,
  SplitMode
} from './groupModel';
import { generateExpenseId } from '../../utils/id';
import { groupStorage } from './storage';
import { formatCurrencyAmount } from '../../utils/currency';
import Decimal from 'decimal.js';

// Expense creation parameters
export interface CreateExpenseParams {
  description: string;
  amount: number; // Amount in cents
  currency: string;
  paidBy: string; // Participant ID
  splitMode: SplitMode;
  participantIds: string[]; // Participants included in this expense
  taxAmount?: number; // Tax in cents
  tipAmount?: number; // Tip in cents
  notes?: string;
}

/**
 * Add an expense to a group
 */
export function addExpense(groupId: string, params: CreateExpenseParams): Group | null {
  const group = groupStorage.loadGroup(groupId);
  if (!group) {
    return null;
  }

  // Validate participants exist
  const validParticipantIds = params.participantIds.filter(id => 
    group.participants.some(p => p.id === id)
  );

  if (validParticipantIds.length === 0) {
    throw new Error('At least one valid participant must be selected');
  }

  // Validate payer exists
  if (!group.participants.some(p => p.id === params.paidBy)) {
    throw new Error('Payer must be a valid participant');
  }

  // Calculate participant shares based on split mode
  const participantShares = calculateParticipantShares(
    validParticipantIds,
    params.amount,
    params.splitMode
  );

  const newExpense: Expense = {
    id: generateExpenseId(),
    description: params.description.trim(),
    amount: params.amount,
    currency: params.currency,
    paidBy: params.paidBy,
    splitMode: params.splitMode,
    participantShares,
    taxAmount: params.taxAmount,
    tipAmount: params.tipAmount,
    notes: params.notes?.trim(),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };

  const updatedGroup: Group = {
    ...group,
    expenses: [...group.expenses, newExpense],
    updatedAt: new Date().toISOString()
  };

  // Recalculate balances
  const groupWithBalances = calculateGroupBalances(updatedGroup);

  groupStorage.saveGroup(groupWithBalances);
  return groupWithBalances;
}

/**
 * Calculate participant shares for an expense
 */
function calculateParticipantShares(
  participantIds: string[],
  totalAmount: number,
  splitMode: SplitMode
): ParticipantShare[] {
  const shares: ParticipantShare[] = [];

  switch (splitMode) {
    case SplitMode.EQUAL:
      const equalAmount = Math.floor(totalAmount / participantIds.length);
      const remainder = totalAmount - (equalAmount * participantIds.length);

      participantIds.forEach((participantId, index) => {
        // Give remainder to first participant(s)
        const amount = equalAmount + (index < remainder ? 1 : 0);
        
        shares.push({
          participantId,
          amount,
          isIncluded: true
        });
      });
      break;

    case SplitMode.PERCENTAGE:
      // For now, default to equal percentages
      const equalPercentage = new Decimal(100).div(participantIds.length);
      
      participantIds.forEach(participantId => {
        shares.push({
          participantId,
          percentage: equalPercentage.toNumber(),
          amount: Math.floor(totalAmount * equalPercentage.div(100).toNumber()),
          isIncluded: true
        });
      });
      break;

    case SplitMode.SHARES:
      // For now, default to equal shares (weight of 1 each)
      participantIds.forEach(participantId => {
        shares.push({
          participantId,
          shares: 1,
          amount: Math.floor(totalAmount / participantIds.length),
          isIncluded: true
        });
      });
      break;

    case SplitMode.CUSTOM:
      // For now, default to equal amounts
      const customAmount = Math.floor(totalAmount / participantIds.length);
      participantIds.forEach(participantId => {
        shares.push({
          participantId,
          amount: customAmount,
          isIncluded: true
        });
      });
      break;

    default:
      throw new Error(`Unsupported split mode: ${splitMode}`);
  }

  return shares;
}

/**
 * Calculate balances for all participants in a group
 */
export function calculateGroupBalances(group: Group): Group {
  const balances: Record<string, number> = {};

  // Initialize all participants with 0 balance
  group.participants.forEach(participant => {
    balances[participant.id] = 0;
  });

  // Calculate balances from expenses (only expenses in group's default currency)
  group.expenses.forEach(expense => {
    // Only include expenses in the group's default currency for balance calculation
    if (expense.currency === group.defaultCurrency) {
      // Person who paid gets credited
      balances[expense.paidBy] += expense.amount;

      // People who owe get debited
      expense.participantShares.forEach(share => {
        if (share.isIncluded && share.amount) {
          balances[share.participantId] -= share.amount;
        }
      });
    }
  });

  return {
    ...group,
    balances
  };
}

/**
 * Get net balance for a specific participant
 */
export function getParticipantBalance(group: Group, participantId: string): number {
  return group.balances?.[participantId] || 0;
}

/**
 * Check if a participant owes money (negative balance)
 */
export function participantOwes(group: Group, participantId: string): boolean {
  return getParticipantBalance(group, participantId) < 0;
}

/**
 * Check if a participant is owed money (positive balance)
 */
export function participantIsOwed(group: Group, participantId: string): boolean {
  return getParticipantBalance(group, participantId) > 0;
}

/**
 * Format amount for display using proper currency formatting
 */
export function formatAmount(amountInCents: number, currency: string = 'USD'): string {
  return formatCurrencyAmount(amountInCents, currency);
}

/**
 * Convert dollar amount to cents for storage
 */
export function dollarsToCents(dollars: number): number {
  return Math.round(dollars * 100);
}

/**
 * Convert cents to dollars for display
 */
export function centsToDollars(cents: number): number {
  return cents / 100;
}

/**
 * Validate expense parameters
 */
export function validateExpenseParams(params: CreateExpenseParams): void {
  if (!params.description?.trim()) {
    throw new Error('Description is required');
  }

  if (params.description.trim().length > 200) {
    throw new Error('Description is too long (max 200 characters)');
  }

  if (!params.amount || params.amount <= 0) {
    throw new Error('Amount must be greater than 0');
  }

  if (params.amount > 100000000) { // $1M in cents
    throw new Error('Amount is too large');
  }

  if (!params.currency || params.currency.length !== 3) {
    throw new Error('Invalid currency code');
  }

  if (!params.paidBy) {
    throw new Error('Payer is required');
  }

  if (!params.participantIds || params.participantIds.length === 0) {
    throw new Error('At least one participant must be selected');
  }

  if (params.taxAmount && params.taxAmount < 0) {
    throw new Error('Tax amount cannot be negative');
  }

  if (params.tipAmount && params.tipAmount < 0) {
    throw new Error('Tip amount cannot be negative');
  }
}
